import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useExport } from './components/header/exporters';
import type { ArchiveView } from './utils/archiveFilter';
import LoadingSpinner from './components/LoadingSpinner';
import { useLocation, useNavigate } from 'react-router-dom';
import './styles/global/App.css';
import './styles/global/index.css';
import Header from './components/Header';
import VendorTable from './components/VendorTable';
import BudgetTracker from './components/BudgetTracker';
import BudgetVarianceTracker from './components/BudgetVarianceTracker';
import { BUDGET_ENABLED, PROCUREMENT_ENABLED, ASOEBI_ENABLED } from './constants/featureFlags';
import VendorComparison from './components/VendorComparison';
import BudgetCalculator from './components/BudgetCalculator';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import ProjectsView from './components/ProjectsView';
import PipelineView from './components/PipelineView';
import OverviewView from './components/OverviewView';
import ClientsView from './components/ClientsView';
import ClientInvoicesView from './components/ClientInvoicesView';
import ClientQuotesView from './components/ClientQuotesView';
import AccountShell from './components/AccountShell';
import AccountCalendarView from './components/AccountCalendarView';
import VendorDirectoryView from './components/VendorDirectoryView';
import { WorkspaceSettingsPage, WorkspaceTeamPage } from './components/account/WorkspaceAdminPages';
import ClientFormPage from './components/account/ClientFormPage';
import InvoiceFormPage from './components/account/InvoiceFormPage';
import InvoicePaymentFormPage from './components/account/InvoicePaymentFormPage';
import QuoteFormPage from './components/account/QuoteFormPage';
import LeadFormPage from './components/account/LeadFormPage';
import { matchAccountRoute, accountNavItem } from './utils/accountRoutes';
import EditProjectPage from './components/account/EditProjectPage';
import EditPersonPage from './components/account/EditPersonPage';
import type { CurrencyCode } from './utils/currency';
import MenuView from './components/MenuView';
import BundlesView from './components/BundlesView';
import AsoebiManager from './components/AsoebiManager';
import AsoebiAccessGate from './components/AsoebiAccessGate';
import AsoebiAssignmentsView from './components/AsoebiAssignmentsView';
import { ASOEBI_TAB_BY_VIEW, ASOEBI_VIEW_BY_TAB, AsoebiTab } from './constants/asoebiTabs';
import LogisticsManager from './components/LogisticsManager';
import NotificationProvider, { useNotifications } from './components/NotificationProvider';
import ConfirmProvider from './components/ui/ConfirmProvider';
import { SearchProvider } from './contexts/SearchContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useRouteRefResolution } from './router/useRouteRefResolution';
import RouteRefNotice from './router/RouteRefNotice';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { ProjectProvider } from './contexts/ProjectContext';
import RecordRefsLoader from './components/RecordRefsLoader';
import { AuditLogProvider, useAuditLog } from './contexts/AuditLogContext';

import CalendarView from './components/CalendarView';
import Settings, { SettingsActions } from './components/Settings';
import { VenueManager } from './components/VenueManager';
import VenueFormPage from './components/forms/VenueFormPage';
import ProjectTimeline from './components/ProjectTimeline';
import ProtectedRoute from './components/ProtectedRoute';
import { useRole } from './hooks/useRole';
import LoginPage from './components/LoginPage';
import KioskPage from './components/kiosk/KioskPage';
import SubscriptionGate from './components/SubscriptionGate';
import SetPasswordPage from './components/SetPasswordPage';
import MusicView from './components/MusicView';
import ActivityTracker from './components/ActivityTracker';
import { entityPath, ENTITY_PREFIX } from './utils/entityRoutes';
import StationeryTracker from './components/StationeryTracker';
import GuidelinesManager from './components/GuidelinesManager';
import EventsList from './components/EventsList';
import EventDetail from './components/EventDetail';
import { EventFormPage } from './components/forms/EventFormPage';
import { EVENTS_ENABLED } from './constants/featureFlags';
import { getGuidelineById } from './services/GuidelinesService';
import DecorPlanner from './components/DecorPlanner';
import InventoryTracker from './components/InventoryTracker';
import { loadInventoryVendors, deleteInventoryVendor } from './services/InventoryService';
import PaymentsView from './components/PaymentsView';
import EventDayDashboard from './components/event-day/EventDayDashboard';
import ProjectSetupPrompt from './components/ProjectSetupPrompt';
import HistoryPage from './components/HistoryPage';
import QuotesTracker from './components/QuotesTracker';
import ShippingAgentEdit from './components/ShippingAgentEdit';
import ProcurementTracker from './components/ProcurementTracker';
import ProcurementOrderEdit from './components/ProcurementOrderEdit';
import { VENDOR_BOOKINGS_ENABLED } from './constants/featureFlags';
import VendorBookingsList from './components/VendorBookingsList';
import { GIFT_REGISTRY_ENABLED } from './constants/featureFlags';
import EmptyState from './components/EmptyState';
import GiftRegistryList from './components/giftRegistry/GiftRegistryList';
import GiftItemPage from './components/giftRegistry/GiftItemPage';
import PublicGiftRegistryPage from './components/giftRegistry/PublicGiftRegistryPage';
import { giftRefFromPath, matchPublicRegistryPath } from './utils/giftRegistry';
import VendorBookingEdit from './components/VendorBookingEdit';
import ShipmentsView from './components/ShipmentsView';
import ShipmentEdit from './components/ShipmentEdit';
import ShippingAgentsView from './components/ShippingAgentsView';
import ProductCatalog from './components/ProductCatalog';
import { VehicleFormPage, StationeryItemFormPage, BudgetCategoryFormPage, SongFormPage, DriverFormPage, GuidelineFormPage, MenuItemFormPage, CalendarEventFormPage, TaskFormPage, VendorFormPage, DecorElementFormPage, RecordPaymentFormPage, ContributorFormPage, CatalogItemFormPage } from './components/FormPage';
import { fmtId, parseId } from './utils/jiraId';
import { ParsedVendor } from './data/vendorData';
import {
  getLastScrapeTimestamp,
  formatScrapeTimestamp,
  setLastScrapeTimestamp
} from './data/scraperIntegration';
import { loadVendorData, loadArchivedVendors, getVendorStats } from './data/VendorLoader';
import { apiPost, planningPath } from './services/PlanningApiClient';
import { refreshBudgetFromApi, apiDeleteBudgetCategory, BUDGET_CHANGE_EVENT } from './utils/budgetStorage';
import { BudgetCategory } from './types/ui/Budget';
import { getAdminPathForView } from './utils/adminNavigation';
import { usePlanningSync } from './hooks/usePlanningSync';
import GuestsPage from './components/guests/GuestsPage';
import { GUESTS_ENABLED } from './constants/featureFlags';
import { useStorageSync } from './hooks/useStorageSync';
import { loadEvents, StoredEvent } from './services/EventsService';
import { loadVenuesFromApi } from './services/VenueApiService';
import { createTask, updateTask, loadTasks, Task, registerGlobalAuditLogCallback } from './services/TasksService';
import { loadDecorItems } from './services/DecorService';
import { refreshBundlesFromApi } from './services/BundleService';
import ConfirmDialog from './components/ui/ConfirmDialog';
import CommunicationsRoute from './components/communications/CommunicationsRoute';
import { COMMUNICATIONS_ENABLED, TEAM_ENABLED, VENUES_ENABLED } from './constants/featureFlags';
import TeamRoute from './components/team/TeamRoute';
import { TeamMemberFormPage } from './components/forms/TeamMemberFormPage';
import { getActiveProjectId } from './utils/session';
import {
  LOGISTICS_TABS,
  EVENT_DAY_TABS,
} from './constants/tabDefinitions';

// Logistics tab <-> URL slug mapping: internal tab values stay as-is ('assignments' etc., see
// LogisticsManager's Tab type) — only the URL segment and the label shown
// in the UI use the renamed 'schedule' wording (see Header.tsx breadcrumb).
const LOGISTICS_TAB_SLUGS: Record<string, string> = Object.fromEntries(
  LOGISTICS_TABS.map(t => [t.value, t.slug ?? t.value])
);
const LOGISTICS_SLUG_TABS: Record<string, string> = Object.fromEntries(
  Object.entries(LOGISTICS_TAB_SLUGS).map(([tab, slug]) => [slug, tab])
);

function AppContent() {
  const { isAuthenticated, isLoading, user } = useAuth();
  // Short addresses (PO-130, /project/PJ-x/...) that need a lookup or another project before the page can open.
  const routeRefs = useRouteRefResolution();
  // Budget screens: switched on, and only for someone who may see money.
  const { canViewFinancials } = useRole();
  const budgetVisible = BUDGET_ENABLED && canViewFinancials;
  const { isProfileLoading } = useSettings();
  const { syncing, triggerSync } = usePlanningSync();
  const { addEntry } = useAuditLog();

  // Register audit log callback on mount
  useEffect(() => {
    registerGlobalAuditLogCallback((action: string, details: Record<string, unknown>) => {
      addEntry({
        action,
        user: user?.email || 'Anonymous',
        page: 'Tasks',
        ...details,
      });
    });
  }, [addEntry, user]);

  // Cross-tab sync: when another tab writes to a known localStorage key,
  // dispatch the appropriate custom event so components reload their data.
  const handleStorageSync = useCallback((key: string) => {
    const keyToEvent: Record<string, string> = {
      anojeje_events: 'anojeje:events-changed',
    };

    const eventName = keyToEvent[key] ?? 'anojeje:storage-sync';
    window.dispatchEvent(
      new CustomEvent(eventName, { detail: { key } })
    );
  }, []);

  useStorageSync(handleStorageSync);

  const location = useLocation();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<ParsedVendor[]>([]);
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>([]);
  const [lastScrapeTime, setLastScrapeTime] = useState<string | null>(null);
  const [searchTerm] = useState('');
  const [, setIsLoadingData] = useState(true);
  const [timelineView, setTimelineView] = useState<'gantt' | 'sequence' | 'run-of-show'>('gantt');
  const [rosEventFilter, setRosEventFilter] = useState('ALL');
  const [rosEventTitles, setRosEventTitles] = useState<string[]>([]);
  const [paymentFilterStatus, setPaymentFilterStatus] = useState('all');
  const [paymentSortField, setPaymentSortField] = useState('balance');
  const [triggerAddItem, setTriggerAddItem] = useState(false);
  const [stationeryCategory, setStationeryCategory] = useState('All');
  const [stationeryStatus, setStationeryStatus] = useState('All');
  const [stationeryArchived, setStationeryArchived] = useState(false);
  const [stationeryView, setStationeryView] = useState<'list' | 'kanban'>('list');
  const [decorCategoryFilter, setDecorCategoryFilter] = useState('ALL');
  const [decorStatusFilter, setDecorStatusFilter] = useState('ALL');
  const [decorEventFilter, setDecorEventFilter] = useState('');
  const [calendarControls, setCalendarControls] = useState<{
    viewMode: 'week' | 'month'; label: string;
    prev: () => void; next: () => void; today: () => void;
    setViewMode: (m: 'week' | 'month') => void;
  } | null>(null);
  const [musicTab, setMusicTab] = useState('After Party');
  const [settingsActions, setSettingsActions] = useState<SettingsActions | null>(null);
  const [triggerAddAssignment, setTriggerAddAssignment] = useState(false);
  const [menuTab, setMenuTab] = useState('Traditional');
  const [menuView, setMenuView] = useState<'card' | 'list'>('card');
  // The URL is the single source of truth for the budget event filter. It was
  // read as `urlParam || budgetEventFilter`, so once ?event= was in the address
  // bar the dropdown could never override it and picking a different event
  // silently did nothing. The change handler writes back to the URL so the
  // control, the address bar and the figures always agree.
  const [budgetEventFilter, setBudgetEventFilter] = useState('');
  const [paymentsEventFilter, setPaymentsEventFilter] = useState('');
  const [budgetView, setBudgetView] = useState<'cards' | 'table'>('cards');
  const [triggerAddBudgetCategory, setTriggerAddBudgetCategory] = useState(false);
  const [appEvents, setAppEvents] = useState<StoredEvent[]>(() => { try { return loadEvents(); } catch { return []; } });
  const [vendorView, setVendorView] = useState<'table' | 'cards'>('table');
  const [vendorStatusFilter, setVendorStatusFilter] = useState<'active' | 'archived'>('active');
  // Archived vendors are a separate fetch — /vendors only ever returns the
  // unarchived ones, so filtering the loaded list by `archived` could never
  // match anything. Refetched each time the filter is switched on so a
  // just-archived vendor shows up immediately.
  const [archivedVendors, setArchivedVendors] = useState<ParsedVendor[]>([]);
  useEffect(() => {
    if (vendorStatusFilter !== 'archived') return;
    let cancelled = false;
    void loadArchivedVendors().then(list => { if (!cancelled) setArchivedVendors(list); });
    return () => { cancelled = true; };
  }, [vendorStatusFilter]);
  const [venueStatusFilter, setVenueStatusFilter] = useState<ArchiveView>('active');
  const [vendorCategoryFilter, setVendorCategoryFilter] = useState('');
  const [songFormCanSave, setSongFormCanSave] = useState(false);
  const songFormRef = useRef<{ save: () => void } | null>(null);
  const [activityFilter, setActivityFilter] = useState('All');
  const ACTIVITY_VIEWS = ['list', 'kanban', 'assignee'] as const;
  const [myTasksOnly, setMyTasksOnly] = useState(false);
  const [priorityFilters, setPriorityFilters] = useState<string[]>([]);
  const [assigneeFilters, setAssigneeFilters] = useState<string[]>([]);
  const [taskStatusFilter, setTaskStatusFilter] = useState<ArchiveView>('active');
  // Separate from taskStatusFilter (archived/active) — this is the task's
  // own workflow status. Defaults to hiding Done, since a finished task
  // clutters a list someone is using to see what's still outstanding.
  // 'Blocked' dropped from this default set (2026-09-19): it's a separate
  // blockedOn facet now, not a status — a blocked task already shows up here
  // under its real Not Started/In Progress status.
  const [taskWorkflowStatusFilter, setTaskWorkflowStatusFilter] = useState<string[]>(['Not Started', 'In Progress']);
  const [triggerAddTask, setTriggerAddTask] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const setSidebarCollapsed = useCallback((v: boolean) => {
    setSidebarCollapsedState(v);
    localStorage.setItem('sidebarCollapsed', String(v));
  }, []);
  // Event Day fullscreen: the sidebar used to only collapse to its narrow
  // icon rail here, still fully visible — defeating the point of an
  // on-the-day expanded view. Not persisted; this is a transient per-session
  // state, unlike the user's own collapsed/expanded preference above.
  const [eventDayFullscreen, setEventDayFullscreen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [logisticsTab, setLogisticsTab] = useState('assignments');
  const [logisticsAssignmentFormOpen, setLogisticsAssignmentFormOpen] = useState(false);
  const [logisticsAssignmentIsEditing, setLogisticsAssignmentIsEditing] = useState(false);
  const [assignmentFormStatus, setAssignmentFormStatus] = useState('pending');
  const [driverName, setDriverName] = useState<string | null>(null);
  const [vehicleName, setVehicleName] = useState<string | null>(null);
  const [, setVendorOrderCount] = useState(0);
  const [vendorRefreshFn, setVendorRefreshFn] = useState<(() => void) | null>(null);
  const ED_TABS = EVENT_DAY_TABS.map(t => t.value);
  const [edWaiterMode, setEdWaiterMode] = useState<'orders' | 'assignments'>('orders');
  const [edHeatmap, setEdHeatmap] = useState(false);
  const [edSelectedEventId, setEdSelectedEventId] = useState<string | null>(null);
  const [edKbViewMode, setEdKbViewMode] = useState<'kanban' | 'batch'>('kanban');
  const [edPhase, setEdPhase] = useState(0);
  const [budgetCategoryTab, setBudgetCategoryTab] = useState<'overview' | 'vendors' | 'purchases' | 'payments'>('vendors');
  const [triggerAddBudgetVendor, setTriggerAddBudgetVendor] = useState(false);
  const [triggerSaveBudgetCategory, setTriggerSaveBudgetCategory] = useState(false);
  const [isEditingBudgetVendor, setIsEditingBudgetVendor] = useState(false);
  const [triggerSaveBudgetVendor, setTriggerSaveBudgetVendor] = useState(false);
  const [triggerCancelBudgetVendor, setTriggerCancelBudgetVendor] = useState(false);
  const [deleteBudgetCategoryConfirm, setDeleteBudgetCategoryConfirm] = useState(false);

  // The Favour Registry was retired; old bookmarks (/favors, /favors/assignments,
  // /favors/bundles) land on Inventory rather than a 404/Dashboard.
  useEffect(() => {
    if (location.pathname === '/favors' || location.pathname.startsWith('/favors/')) {
      navigate('/procurement/inventory', { replace: true });
    }
  }, [location.pathname, navigate]);

  // /logistics/<tab-slug> keeps the URL and breadcrumb in sync with the
  // active Logistics tab (same convention as the /bundle/ effect above).
  // Landing on bare /logistics (no sub-path) defaults to Schedule —
  // redirect immediately so the URL bar reflects it.
  useEffect(() => {
    if (location.pathname === '/logistics') {
      navigate('/logistics/schedule', { replace: true });
      return;
    }
    if (location.pathname.startsWith('/logistics/')) {
      const slug = location.pathname.split('/logistics/')[1]?.split('/')[0];
      const tab = slug ? LOGISTICS_SLUG_TABS[slug] : undefined;
      if (tab) setLogisticsTab(tab);
    }
  }, [location.pathname]);

  // Shared by LogisticsManager's own tab bar and Header's breadcrumb
  // dropdown so both push the matching /logistics/<slug> URL.
  const handleLogisticsTabChange = useCallback((tab: string) => {
    setLogisticsTab(tab);
    navigate(`/logistics/${LOGISTICS_TAB_SLUGS[tab] ?? tab}`);
  }, [navigate]);

  const getActiveViewFromPath = (pathname: string): string => {
    const adminPath = pathname;
    
    // Create/edit form routes (must come before detail routes)
    if (adminPath === '/task/new') return 'task-new';
    if (adminPath.startsWith('/task/') && adminPath !== '/task/new') return 'task-edit';
    if (adminPath === '/vehicle/new') return 'vehicle-new';
    if (adminPath.startsWith('/vehicle/') && adminPath !== '/vehicle/new') return 'vehicle-detail';
    // Bundle create/edit form — reuses the 'asoebi' view (Bundles
    // tab) rather than a dedicated activeView, since the bundle form is an
    // inline panel within AsoebiManager, not a standalone page. bundleId /
    // bundleIsNewRoute (below) tell BundlesView which form to auto-open.
    if (adminPath.startsWith('/bundle/')) return ASOEBI_ENABLED ? 'asoebi-bundles' : 'asoebi';
    if (adminPath === '/stationery-item/new') return 'stationery-item-new';
    if (adminPath === '/budget-category/new') return 'budget-category-new';
    if (adminPath === '/song/new') return 'song-new';
    if (adminPath === '/driver/new') return 'driver-new';
    if (adminPath.startsWith('/driver/') && adminPath !== '/driver/new') return 'driver-detail';
    // '/inventory/product/new' retired — an inventory product is a catalogue
    // item that has stock, which Product/CatalogItemFormPage already models.
    if (adminPath === '/inventory/product/new') return 'catalogue-new';
    if (adminPath === '/menu-item/new' || adminPath === '/menu-item/new/') return 'menu-item-new';
    if (adminPath.startsWith('/menu-item/') && !adminPath.endsWith('/new') && !adminPath.endsWith('/new/')) return 'menu-item-edit';
    if (adminPath === '/calendar/event/new') return 'calendar-event-new';
    if (adminPath.startsWith('/calendar/event/') && adminPath !== '/calendar/event/new') return 'calendar-event-edit';
    if (EVENTS_ENABLED) {
      if (adminPath === '/events' || adminPath === '/events/') return 'events';
      if (adminPath === '/events/new') return 'event-new';
      if (adminPath.startsWith('/events/')) return 'event-detail';
    }
    if (adminPath === '/guideline/new') return 'guideline-new';
    if (adminPath.startsWith('/guideline/') && adminPath !== '/guideline/new') return 'guideline-edit';
    if (adminPath.startsWith('/guidelines/') && adminPath !== '/guidelines/new') return 'guideline-edit';
    if (adminPath === '/guidelines') return 'guidelines';

    if (adminPath === '/vendor/suggest') return 'vendor-suggestion';
    if (adminPath.startsWith('/vendor/') && adminPath.endsWith('/profile')) return 'vendor-profile';
    if (adminPath.startsWith('/vendor/') && adminPath.endsWith('/rate')) return 'vendor-rating';

    if (adminPath === '/dashboard') return 'dashboard';
    if (GUESTS_ENABLED && (adminPath === '/guests' || adminPath.startsWith('/guests/'))) return 'guests';
    // Account-level screens and their create/edit pages (see accountRoutes.ts)
    const accountRoute = matchAccountRoute(adminPath);
    if (accountRoute) return accountRoute.view;
    if (ASOEBI_ENABLED) {
      if (adminPath === '/asoebi/fabrics' || adminPath.startsWith('/asoebi/fabrics/')) return 'asoebi-fabrics';
      if (adminPath === '/asoebi/orders' || adminPath.startsWith('/asoebi/orders/')) return 'asoebi-orders';
      if (adminPath === '/asoebi/assignments' || adminPath.startsWith('/asoebi/assignments/')) return 'asoebi-assignments';
      if (adminPath === '/asoebi/bundles') return 'asoebi-bundles';
    }
    if (adminPath === '/asoebi' || adminPath.startsWith('/asoebi/')) return 'asoebi';
    if (adminPath === '/menu') return 'menu';
    if (adminPath === '/budget') return 'budget';
    if (adminPath === '/budget-variances') return 'budget-variances';
    if (adminPath.startsWith('/budget/category/')) return 'budget-category';
    if (adminPath === '/budget-configurator') return 'calculator';
    if (VENUES_ENABLED && adminPath === '/venue') return 'venue';
    if (VENUES_ENABLED && adminPath === '/venue/new') return 'venue-new';
    if (VENUES_ENABLED && adminPath.startsWith('/venue/')) return 'venue-detail';
    if (GIFT_REGISTRY_ENABLED && adminPath === '/gift-registry') return 'gift-registry';
    if (GIFT_REGISTRY_ENABLED && adminPath === '/gift-registry/new') return 'gift-registry-new';
    if (GIFT_REGISTRY_ENABLED && adminPath.startsWith('/gift-registry/')) return 'gift-registry-item';
    if (VENDOR_BOOKINGS_ENABLED && adminPath === '/vendor-bookings') return 'vendor-bookings';
    if (VENDOR_BOOKINGS_ENABLED && adminPath.startsWith('/vendor-bookings/')) return 'vendor-booking-edit';
    if (adminPath === '/vendors') return 'vendors';
    if (adminPath === '/vendors/compare') return 'vendor-compare';
    if (adminPath.startsWith('/vendor/')) return 'vendor-detail';
    if (adminPath === '/calendar') return 'calendar';
    if (COMMUNICATIONS_ENABLED && adminPath === '/communications') return 'communications';
    if (TEAM_ENABLED && adminPath === '/team') return 'team';
    if (TEAM_ENABLED && adminPath.startsWith('/team-member/')) return 'team-member-form';
    if (adminPath === '/settings' || adminPath.startsWith('/settings/')) return 'settings';
    if (adminPath === '/timeline') return 'timeline';
    if (adminPath === '/logistics' || adminPath.startsWith('/logistics/')) return 'logistics';
    if (adminPath === '/music') return 'music';
    if (adminPath === '/activities' || adminPath.startsWith('/activities/')) return 'activities';
    if (adminPath === '/stationery') return 'stationery';
    if (adminPath.startsWith('/stationery-item/') && adminPath !== '/stationery-item/new') return 'stationery-item-edit';
    if (adminPath === '/decor') return 'decor';
    if (adminPath === '/decor/new') return 'decor-new';
    if (adminPath.startsWith('/decor/') && adminPath !== '/decor/new') return 'decor-edit';
    if (PROCUREMENT_ENABLED && adminPath === '/procurement/orders') return 'procurement-orders';
    if (PROCUREMENT_ENABLED && adminPath === '/procurement/orders/new') return 'procurement-order-new';
    if (PROCUREMENT_ENABLED && adminPath.startsWith('/procurement/orders/')) return 'procurement-order-edit';
    if (PROCUREMENT_ENABLED && adminPath === '/procurement/shipments') return 'procurement-shipments';
    if (PROCUREMENT_ENABLED && adminPath === '/procurement/shipments/new') return 'procurement-shipment-new';
    if (PROCUREMENT_ENABLED && adminPath.startsWith('/procurement/shipments/')) return 'procurement-shipment-edit';
    if (adminPath === '/procurement' || adminPath === '/procurement/inventory') return 'procurement';
    if (adminPath.startsWith('/procurement/shipping-agents/')) return 'shipping-agent-edit';
    if (adminPath === '/procurement/shipping') return 'shipping';
    if (adminPath === '/payments' || adminPath === '/payments/contributors' || adminPath === '/payments/settlement') return 'payments';
    if (adminPath === '/payments/record/new') return 'payment-record';
    if (adminPath.startsWith('/payments/record/') && adminPath !== '/payments/record/new') return 'payment-record';
    if (adminPath === '/payments/contributor/new') return 'contributor-form';
    if (adminPath.startsWith('/payments/contributor/') && adminPath !== '/payments/contributor/new') return 'contributor-form';
    if (adminPath === '/event-day' || adminPath.startsWith('/event-day/')) return 'event-day';
    if (adminPath === '/quotes') return 'quotes';
    if (adminPath === '/quotes/new') return 'quote-new';
    if (adminPath.startsWith('/quotes/')) return 'quote-detail';
    if (adminPath === '/catalogue' || adminPath === '/catalog') return 'catalogue';
    if (adminPath === '/catalogue/new' || adminPath === '/catalog/new') return 'catalogue-new';
    if ((adminPath.startsWith('/catalogue/') || adminPath.startsWith('/catalog/')) && !adminPath.endsWith('/new')) return 'catalogue-edit';
    if (adminPath === '/history') return 'history';
    // Root lands on whichever project you were last in — matches Kalligo
    // remembering your last project instead of always re-showing the
    // account shell. With no remembered project yet (first login), land
    // on "At a glance" — Kalligo's own true landing page, confirmed live —
    // rather than Projects.
    if (adminPath === '/' || adminPath === '') {
      return getActiveProjectId() ? 'dashboard' : 'overview';
    }
    return 'dashboard';
  };

  const activeView = getActiveViewFromPath(location.pathname);

  // Extract IDs from URLs for individual record pages
  const adminPath = location.pathname;
  const shippingAgentId = activeView === 'shipping-agent-edit' ? parseId('SA', adminPath.split('/procurement/shipping-agents/')[1]) : undefined;
  const procurementOrderId = activeView === 'procurement-order-edit' ? parseId('PO', adminPath.split('/procurement/orders/')[1]) : undefined;
  const giftRef = activeView === 'gift-registry-item' ? giftRefFromPath(adminPath) : null;
  const vendorBookingId = activeView === 'vendor-booking-edit' ? parseId('BK', adminPath.split('/vendor-bookings/')[1]) : undefined;
  const procurementShipmentId = activeView === 'procurement-shipment-edit' ? parseId('SH', adminPath.split('/procurement/shipments/')[1]) : undefined;
  const paymentsTab: 'payments' | 'contributors' | 'settlement' =
    adminPath === '/payments/contributors' ? 'contributors' :
    adminPath === '/payments/settlement' ? 'settlement' : 'payments';
  const handlePaymentsTabChange = (t: 'payments' | 'contributors' | 'settlement') =>
    navigate(t === 'payments' ? '/payments' : `/payments/${t}`);
  const edTab: typeof ED_TABS[number] = (() => {
    const seg = adminPath.startsWith('/event-day/') ? adminPath.split('/event-day/')[1] : '';
    return (ED_TABS as readonly string[]).includes(seg) ? (seg as typeof ED_TABS[number]) : 'overview';
  })();
  const handleEdTabChange = (tab: typeof ED_TABS[number]) =>
    navigate(tab === 'overview' ? '/event-day' : `/event-day/${tab}`);
  const activityView: typeof ACTIVITY_VIEWS[number] = (() => {
    const seg = adminPath.startsWith('/activities/') ? adminPath.split('/activities/')[1] : '';
    return (ACTIVITY_VIEWS as readonly string[]).includes(seg) ? (seg as typeof ACTIVITY_VIEWS[number]) : 'list';
  })();
  const handleActivityViewChange = (view: typeof ACTIVITY_VIEWS[number]) =>
    navigate(view === 'list' ? '/activities' : `/activities/${view}`);
  const venueId = activeView === 'venue-detail' ? parseId('VN', adminPath.split('/venue/')[1]) : undefined;
  const vendorId = (activeView === 'vendor-detail') ? parseId('VD', adminPath.split('/vendor/')[1]) : undefined;
  const vendorProfileId = (activeView === 'vendor-profile') ? parseId('VD', adminPath.split('/vendor/')[1]?.replace('/profile', '')) : undefined;
  const vendorRatingId = (activeView === 'vendor-rating') ? parseId('VD', adminPath.split('/vendor/')[1]?.replace('/rate', '')) : undefined;
  const budgetCategoryId = activeView === 'budget-category' ? parseId('BC', adminPath.split('/budget/category/')[1]) : undefined;
  const fabricDetailId = activeView === 'asoebi-fabrics' ? parseId(ENTITY_PREFIX.fabric, adminPath.split('/asoebi/fabrics/')[1]) : undefined;
  const quoteDetailId = activeView === 'quote-detail' ? parseId('QT', adminPath.split('/quotes/')[1]) : undefined;
  const catalogueItemId = activeView === 'catalogue-edit' ? parseId('CT', adminPath.split(adminPath.startsWith('/catalogue/') ? '/catalogue/' : '/catalog/')[1]) : undefined;


  const budgetCategoryName = budgetCategoryId
    ? budgetCategories.find(c => String(c.id) === String(budgetCategoryId))?.name ?? null
    : null;
  const vehicleId = activeView === 'vehicle-detail' ? parseId('VH', adminPath.split('/vehicle/')[1]) : undefined;
  const driverId = activeView === 'driver-detail' ? parseId('DR', adminPath.split('/driver/')[1]) : undefined;
  const taskEditId = activeView === 'task-edit'
    ? parseId('TA', adminPath.split('/task/')[1])
    : undefined;

  // Load editing task from API only — DB is source of truth, never use localStorage cache.
  // isLoadingEditTask distinguishes "still fetching" from "new task, no id yet" — both
  // look identical as task=undefined otherwise, so a deep link (e.g. from the overdue-
  // task notification) rendered a blank form for the ~1-2s fetch took, reading as if
  // the click had silently failed rather than actually opened the right task.
  const [isLoadingEditTask, setIsLoadingEditTask] = useState(false);
  useEffect(() => {
    if (!taskEditId) { setEditingTask(undefined); setIsLoadingEditTask(false); return; }
    setEditingTask(undefined); // clear while loading
    setIsLoadingEditTask(true);
    import('./services/TasksService').then(({ refreshTasksFromApi }) =>
      refreshTasksFromApi().then(all => {
        const fresh = all.find(t => t.id === taskEditId);
        setEditingTask(fresh);
        setIsLoadingEditTask(false);
      })
    );
  }, [taskEditId]);

  const eventId = activeView === 'event-detail' ? parseId('EV', adminPath.split('/events/')[1]?.split(/[/?#]/)[0]) : undefined;
  const guidelineId = activeView === 'guideline-edit'
    ? parseId('GL', adminPath.split('/guidelines/')[1] || adminPath.split('/guideline/')[1])
    : undefined;
  const guidelineName = guidelineId ? (getGuidelineById(guidelineId)?.title ?? null) : null;
  const stationeryItemId = activeView === 'stationery-item-edit' ? parseId('ST', adminPath.split('/stationery-item/')[1]) : undefined;
  const decorElementId = activeView === 'decor-edit' ? parseId('DC', adminPath.split('/decor/')[1]) : undefined;
  const _decorElement = decorElementId ? loadDecorItems().find(d => d.id === decorElementId) : null;
  const decorElementCategory = _decorElement?.category ?? null;
  const decorElementName = _decorElement?.area ?? null;
  const calendarEventId = activeView === 'calendar-event-edit' ? parseId('CE', adminPath.split('/calendar/event/')[1]) : undefined;
  const vendorName = vendorId ? (vendors.find(v => v.id === vendorId)?.name ?? null) : null;
  const vendorProfileName = vendorProfileId ? (vendors.find(v => v.id === vendorProfileId)?.name ?? null) : null;
  const vendorRatingName = vendorRatingId ? (vendors.find(v => v.id === vendorRatingId)?.name ?? null) : null;
  // Bundle ids are plain backend row ids (no Jira-style prefix) — see
  // BundleService.mapApiRow, so no parseId() needed here.
  const bundleIsNewRoute = adminPath === '/bundle/new';
  const bundleId = adminPath.startsWith('/bundle/') && !bundleIsNewRoute ? adminPath.split('/bundle/')[1] : undefined;
  const [stationeryItemName, setStationeryItemName] = useState<string | null>(null);
  const [stationeryItemArchived, setStationeryItemArchived] = useState(false);
  const [catalogueItemName, setCatalogueItemName] = useState<string | null>(null);
  const [menuItemName, setMenuItemName] = useState<string | null>(null);
  const menuItemId = activeView === 'menu-item-edit' ? (parseId('MI', adminPath.split('/menu-item/')[1]) ?? null) : null;
  const [taskName, setTaskName] = useState<string | null>(null);
  const handleViewChange = (view: string, options?: { eventId?: string | null }) => {
    const path = getAdminPathForView(view, options);
    navigate(path);
  };

  // Event Day's "fullscreen" toggle (Header.tsx) only ever hid its own
  // toolbar and reclaimed .main-content's padding — the app sidebar stayed
  // on screen the whole time, which defeats the point of an on-the-day
  // expanded view. Collapse the sidebar in sync, restoring whatever state
  // it was in beforehand rather than forcing it back open.
  useEffect(() => {
    let preFullscreenCollapsed: boolean | null = null;
    const handler = (e: Event) => {
      const entering = !!(e as CustomEvent<boolean>).detail;
      setEventDayFullscreen(entering);
      if (entering) {
        preFullscreenCollapsed = sidebarCollapsed;
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(preFullscreenCollapsed ?? false);
        preFullscreenCollapsed = null;
      }
    };
    window.addEventListener('event-day-fullscreen', handler);
    return () => window.removeEventListener('event-day-fullscreen', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for passing shipping data between components
  const [shippingExportData, setShippingExportData] = useState<{
    amount: number;
    description: string;
    currency: CurrencyCode;
  } | null>(null);

  // Redirect unknown paths to home
  // No redirect needed — all paths are valid React Router paths



  // Load real vendor data on component mount
  useEffect(() => {
    // One-time: remove legacy INSIDETRACK vendor seeded from old code
    loadInventoryVendors()
      .filter(v => v.name === 'INSIDETRACK POP-UP')
      .forEach(v => deleteInventoryVendor(v.id));

    // Eagerly load bundles from API
    refreshBundlesFromApi().catch(() => {});

    // Warm venue cache so any component can call getCachedVenues() reliably
    loadVenuesFromApi().catch(() => {});

    // Events are no longer fetched here: the /events API is disabled (moved
    // to Kalligo), so only the local cache is read (see appEvents above).
    // Re-sync whenever mutations (create/update/delete) fire anojeje:events-changed
    const syncEvents = () => { try { setAppEvents(loadEvents()); } catch { /* ignore */ } };
    window.addEventListener('anojeje:events-changed', syncEvents);
    // Keep budget categories in sync so the breadcrumb always has the right category name.
    // Only wired to re-fetch on mutation events until now — landing straight on
    // /budget/category/:id (a bookmark, a shared link) never populated
    // budgetCategories at all, so the breadcrumb fell back to the literal
    // word "Category" instead of the real name.
    const syncBudget = () => void refreshBudgetFromApi().then(setBudgetCategories).catch(() => {});
    if (budgetVisible) syncBudget();
    window.addEventListener(BUDGET_CHANGE_EVENT, syncBudget);
    // Navigate to Record Payment form page when Header button fires (skip if event-day hub is active)
    const handleRecordNew = () => { if (activeView === 'event-day') return; navigate('/payments/record/new'); };
    window.addEventListener('payment-record-new', handleRecordNew);
    return () => {
      window.removeEventListener('anojeje:events-changed', syncEvents);
      window.removeEventListener(BUDGET_CHANGE_EVENT, syncBudget);
      window.removeEventListener('payment-record-new', handleRecordNew);
    };
  }, [navigate, activeView, budgetVisible]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingData(true);
        const realVendors = await loadVendorData();
        setVendors(realVendors);
        getVendorStats(realVendors);
        const timestamp = getLastScrapeTimestamp();
        setLastScrapeTime(timestamp);
        if (realVendors.length > 0 && !timestamp) {
          const currentTime = new Date().toISOString();
          setLastScrapeTimestamp(currentTime);
          setLastScrapeTime(currentTime);
        }
      } catch (error) {
        console.error('Error loading vendor data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();

    // When a background cache-refresh completes, update vendor state with fresh data
    const onVendorsRefreshed = (e: Event) => {
      const fresh = (e as CustomEvent<ParsedVendor[]>).detail;
      if (Array.isArray(fresh) && fresh.length) setVendors(fresh);
    };
    window.addEventListener('anojeje:vendors-refreshed', onVendorsRefreshed);
    return () => window.removeEventListener('anojeje:vendors-refreshed', onVendorsRefreshed);
  }, []);

  // Load the sourcing-supplier list (and auto-select one) when the inventory
  // view opens. Re-read on entry because it's localStorage-backed and can be
  // changed by the tracker itself while the app is running.




  const filteredVendors = useMemo(() => (
    vendorStatusFilter === 'archived' ? archivedVendors : vendors
  ).filter(vendor => {
    // The archived set comes from its own fetch above; the active list is
    // already archived-free, so there's nothing left to filter on here.

    // Filter by vendor category (from Settings controlled vocabulary)
    if (vendorCategoryFilter) {
      const categoryLower = vendorCategoryFilter.toLowerCase();
      const matchesCategory = vendor.services.some(service =>
        service.toLowerCase().includes(categoryLower)
      );
      if (!matchesCategory) return false;
    }

    // If no search term, return all vendors (that passed archive + category filter)
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();

    // Check if search term is a rating (e.g., "4+", "5 stars", "above 3")
    const ratingMatch = searchLower.match(/(\d+)\+?|above\s+(\d+)|(\d+)\s*stars?/);
    if (ratingMatch) {
      const rating = parseInt(ratingMatch[1] || ratingMatch[2] || ratingMatch[3]);
      if (rating && rating >= 1 && rating <= 5) {
        return vendor.rating >= rating;
      }
    }

    // Regular text search
    return (
      vendor.name.toLowerCase().includes(searchLower) ||
      vendor.description.toLowerCase().includes(searchLower) ||
      vendor.services.some(service =>
        service.toLowerCase().includes(searchLower)
      )
    );
  }), [vendors, archivedVendors, vendorStatusFilter, vendorCategoryFilter, searchTerm]);

  const handleRateVendor = (vendor: ParsedVendor) => {
    navigate(`/vendor/${fmtId('VD', vendor.id)}/rate`);
  };

  const handleVendorClick = (vendor: ParsedVendor) => {
    navigate(`/vendor/${fmtId('VD', vendor.id)}`);
  };

  const handleSuggestVendor = () => {
    navigate('/vendor/suggest');
  };

  const { showSuccess, showError } = useNotifications();

  // A failed save is left to throw: the vendor form shows a 422's field errors
  // under their fields (or a toast) and stays open, instead of this reporting
  // success for a vendor the server never accepted.
  const handleVendorSuggestionSubmit = async (suggestion: any) => {
    // Also create a real vendor entry so it appears in the vendor list
    const newId = `vendor_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const newVendor: ParsedVendor = {
      id: newId,
      name: suggestion.name || '',
      services: suggestion.services || [],
      description: suggestion.description || '',
      contact: {
        phone: suggestion.contact?.phone || '',
        email: suggestion.contact?.email || '',
        website: suggestion.contact?.website || '',
        instagram: suggestion.contact?.instagram || '',
        address: '',
      },
      rating: suggestion.rating || 0,
      reviewCount: 0,
      priceRange: (suggestion.priceRange || '₦₦') as ParsedVendor['priceRange'],
      availability: suggestion.availability || [],
      specializations: suggestion.specializations || [],
      images: [],
      verified: false,
      featured: false,
      createdAt: new Date(),
      sourceEvents: [],
      instagramHandle: suggestion.contact?.instagram?.replace(/^@/, '') || '',
    };

    // Persist to DB via API
    await apiPost(planningPath('/vendors'), {
      name: newVendor.name,
      description: newVendor.description,
      price_range: newVendor.priceRange,
      phone: newVendor.contact.phone,
      email: newVendor.contact.email,
      website: newVendor.contact.website,
      address: newVendor.contact.address,
      instagram: newVendor.instagramHandle,
      marketplace: suggestion.marketplace || null,
      services: newVendor.services,
      availability: newVendor.availability,
      specializations: newVendor.specializations,
      signal_source: 'manual',
    });

    // Add to in-memory vendor list optimistically
    setVendors(prev => [newVendor, ...prev]);

    showSuccess('Vendor added successfully!');
    navigate('/vendors');
  };

  // Export button (Header) on the vendor list: the vendors it shows right now.
  useExport('vendors', { rows: filteredVendors, ctx: undefined });

  // Public route — must bypass the auth gate below entirely, since a brand
  // new team member has no session yet when they click this link.
  if (window.location.pathname === '/set-password') {
    return <SetPasswordPage />;
  }
  // The event-day kiosk link (/kiosk?event=<id>): its own event-bound session, outside the normal sign-in.
  if (window.location.pathname === '/kiosk') {
    return <KioskPage />;
  }

  // The guests' page of the Gift Registry: the private link in the address is the only key, no sign-in, no sidebar.
  const registryToken = GIFT_REGISTRY_ENABLED ? matchPublicRegistryPath(window.location.pathname) : null;
  if (registryToken) {
    return <PublicGiftRegistryPage token={registryToken} />;
  }

  // Auth and profile were two separate full-page spinners rendered back to
  // back, so a cold load flashed spinner → spinner → the page's own
  // "Loading …". They're one continuous boot now: a single spinner that
  // hands straight over to whatever the view shows while its data arrives.
  if (isLoading || (isAuthenticated && isProfileLoading)) {
    return <LoadingSpinner fullPage />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  if (routeRefs.projectUnavailable || routeRefs.switchFailed) {
    return <RouteRefNotice kind="project" reason={routeRefs.switchFailed ? 'switch-failed' : 'unavailable'} />;
  }
  if (routeRefs.ambiguous) {
    return <RouteRefNotice kind="ambiguous" segment={routeRefs.ambiguous.segment} candidates={routeRefs.ambiguous.candidates} />;
  }
  if (routeRefs.blocking) {
    return <LoadingSpinner fullPage message="Opening…" />;
  }

  // Account-level views (Overview, Projects, Sales Pipeline) sit one level
  // above any single project — shown in their own minimal shell instead of
  // the per-project Sidebar+Header, matching Kalligo's separate Overview/
  // Pipeline/Delivery nav. Picking a project switches workspace and lands
  // you in the full per-project app below.
  const accountRoute = matchAccountRoute(location.pathname);
  if (accountRoute) {
    return (
      <SearchProvider>
        {/* The onboarding wizard stays hidden until events return; an unfinished project is asked for its settings instead. */}
        <ProjectSetupPrompt />
        <AccountShell activeView={accountNavItem(accountRoute.view)}>
          {activeView === 'overview' && <OverviewView />}
          {activeView === 'projects' && <ProjectsView />}
          {activeView === 'account-calendar' && EVENTS_ENABLED && <AccountCalendarView />}
          {activeView === 'pipeline' && <PipelineView />}
          {activeView === 'clients' && <ClientsView />}
          {activeView === 'invoices' && <ClientInvoicesView />}
          {activeView === 'client-quotes' && <ClientQuotesView />}
          {activeView === 'vendor-directory' && <VendorDirectoryView />}
          {activeView === 'workspace-team' && <WorkspaceTeamPage />}
          {activeView === 'workspace-settings' && <WorkspaceSettingsPage />}
          {activeView === 'client-form' && <ClientFormPage key={accountRoute.id ?? 'new'} clientId={accountRoute.id} />}
          {activeView === 'invoice-form' && <InvoiceFormPage key={accountRoute.id ?? 'new'} invoiceId={accountRoute.id} />}
          {activeView === 'invoice-payment-form' && accountRoute.id && <InvoicePaymentFormPage key={accountRoute.id} invoiceId={accountRoute.id} />}
          {activeView === 'client-quote-form' && <QuoteFormPage key={accountRoute.id ?? 'new'} quoteId={accountRoute.id} />}
          {activeView === 'lead-form' && <LeadFormPage />}
          {activeView === 'project-edit' && <EditProjectPage key={accountRoute.id ?? 'none'} projectId={accountRoute.id} tab={accountRoute.tab} />}
          {activeView === 'user-edit' && <EditPersonPage key={accountRoute.id ?? 'none'} personId={accountRoute.id} />}
        </AccountShell>
      </SearchProvider>
    );
  }

  return (
    <SearchProvider>
          <ProjectSetupPrompt />
          <div className="App">
            {/* Unmounted entirely in Event Day fullscreen — collapsing to
                the narrow icon rail (below) still left it on screen. */}
            {!eventDayFullscreen && (
            <Sidebar
              activeView={activeView}
              onViewChange={handleViewChange}
              collapsed={sidebarCollapsed}
              onCollapsedChange={setSidebarCollapsed}
              mobileOpen={mobileSidebarOpen}
              onMobileClose={() => setMobileSidebarOpen(false)}
            />
            )}

        <div className="layout-right">
          <Header
            onMobileMenuToggle={() => setMobileSidebarOpen(prev => !prev)}
            onSuggestVendor={handleSuggestVendor}
            lastScrapeTime={lastScrapeTime ? formatScrapeTimestamp(lastScrapeTime) : null}
            activeView={activeView}
            onViewChange={handleViewChange}
            timelineView={timelineView}
            onTimelineViewChange={setTimelineView}
            rosEventFilter={rosEventFilter}
            onRosEventFilterChange={setRosEventFilter}
            rosEventTitles={rosEventTitles}
            onAddEvent={activeView === 'calendar' ? () => navigate('/calendar/event/new') : undefined}
            onAddVehicle={() => navigate('/vehicle/new')}
            onAddDriver={() => navigate('/driver/new')}
            onAddAssignment={() => setTriggerAddAssignment(true)}
            logisticsAssignmentFormOpen={logisticsAssignmentFormOpen}
            logisticsAssignmentIsEditing={logisticsAssignmentIsEditing}
            assignmentFormStatus={assignmentFormStatus}
            onAssignmentFormStatusChange={setAssignmentFormStatus}
            onCancelAssignment={() => setLogisticsAssignmentFormOpen(false)}
            onAddGuideline={() => navigate('/guideline/new')}
            driverId={driverId}
            driverName={driverName}
            vehicleId={vehicleId}
            vehicleName={vehicleName}
            vendorList={activeView === 'vendor-detail' ? vendors.map(v => ({ id: v.id, name: v.name })) : undefined}
            onVendorNavigate={(id) => navigate(`/vendor/${fmtId('VD', id)}`)}
            vendorName={vendorName}
            vendorProfileId={vendorProfileId}
            vendorProfileName={vendorProfileName}
            vendorRatingId={vendorRatingId}
            vendorRatingName={vendorRatingName}
            stationeryItemName={stationeryItemName}
            stationeryItemArchived={stationeryItemArchived}
            taskArchived={!!editingTask?.archived}
            catalogueItemId={catalogueItemId}
            catalogueItemName={catalogueItemName}
            menuItemName={menuItemName}
            menuItemId={menuItemId}
            guidelineId={guidelineId}
            guidelineName={guidelineName}
            budgetCategoryId={budgetCategoryId}
            budgetCategoryName={budgetCategoryName}
            quoteDetailId={quoteDetailId}
            onContactVendor={() => {
              const v = vendorId ? vendors.find(vv => vv.id === vendorId) : null;
              if (v) handleVendorClick(v);
            }}
            onRateVendorAction={() => {
              const id = vendorId || vendorProfileId;
              const v = id ? vendors.find(vv => vv.id === id) : null;
              if (v) handleRateVendor(v);
            }}
            onSaveVendor={() => {
              window.dispatchEvent(new CustomEvent('vendor-save'));
            }}
            onDeleteVendor={() => {
              window.dispatchEvent(new CustomEvent('vendor-delete'));
            }}
            onFormSave={() => {
              window.dispatchEvent(new CustomEvent('form-save'));
            }}
            onFormDelete={() => {
              window.dispatchEvent(new CustomEvent('form-delete'));
            }}
            paymentFilterStatus={paymentFilterStatus}
            onPaymentFilterStatusChange={setPaymentFilterStatus}
            paymentSortField={paymentSortField}
            onPaymentSortFieldChange={setPaymentSortField}
            paymentsTab={paymentsTab}
            onPaymentsTabChange={handlePaymentsTabChange}
            stationeryCategory={stationeryCategory}
            onStationeryCategoryChange={setStationeryCategory}
            stationeryArchived={stationeryArchived}
            onStationeryArchivedChange={setStationeryArchived}
            stationeryView={stationeryView}
            onStationeryViewChange={setStationeryView}
            decorCategoryFilter={decorCategoryFilter}
            onDecorCategoryFilterChange={setDecorCategoryFilter}
            decorStatusFilter={decorStatusFilter}
            onDecorStatusFilterChange={setDecorStatusFilter}
            decorEventFilter={decorEventFilter}
            onDecorEventFilterChange={setDecorEventFilter}
            decorEvents={appEvents.map(e => ({ id: e.id, title: e.title }))}
            onAddDecorElement={() => navigate('/decor/new')}
            vendorView={vendorView}
            onVendorViewChange={setVendorView}
            vendorStatusFilter={vendorStatusFilter}
            onVendorStatusFilterChange={setVendorStatusFilter}
            venueStatusFilter={venueStatusFilter}
            onVenueStatusFilterChange={setVenueStatusFilter}
            vendorCategoryFilter={vendorCategoryFilter}
            onVendorCategoryFilterChange={setVendorCategoryFilter}
            budgetEventFilter={budgetEventFilter}
            onBudgetEventFilterChange={setBudgetEventFilter}
            budgetEvents={appEvents.map(e => ({ id: e.id, title: e.title }))}
            paymentsEventFilter={paymentsEventFilter}
            onPaymentsEventFilterChange={setPaymentsEventFilter}
            budgetView={budgetView}
            onBudgetViewChange={setBudgetView}
            onAddBudgetCategory={() => navigate('/budget-category/new')}
            budgetCategoryTab={budgetCategoryTab}
            onBudgetCategoryTabChange={setBudgetCategoryTab}
            onAddBudgetVendor={() => setTriggerAddBudgetVendor(true)}
            onSaveBudgetCategory={() => setTriggerSaveBudgetCategory(true)}
            isEditingBudgetVendor={isEditingBudgetVendor}
            onSaveBudgetVendor={() => setTriggerSaveBudgetVendor(true)}
            onCancelBudgetVendor={() => setTriggerCancelBudgetVendor(true)}
            onDeleteBudgetCategory={() => {
              setDeleteBudgetCategoryConfirm(true);
            }}
            onAddItem={() => navigate('/stationery-item/new')}
            onSongSaveClick={() => songFormRef.current?.save()}
            onSongCancelClick={() => navigate('/music')}
            songFormCanSave={songFormCanSave}
            activityFilter={activityFilter}
            onActivityFilterChange={setActivityFilter}
            activityView={activityView}
            onActivityViewChange={handleActivityViewChange}
            myTasksOnly={myTasksOnly}
            onMyTasksToggle={() => setMyTasksOnly(v => !v)}
            priorityFilters={priorityFilters}
            onPriorityFiltersChange={setPriorityFilters}
            assigneeFilters={assigneeFilters}
            onAssigneeFiltersChange={setAssigneeFilters}
            taskStatusFilter={taskStatusFilter}
            onTaskStatusFilterChange={setTaskStatusFilter}
            taskWorkflowStatusFilter={taskWorkflowStatusFilter}
            onTaskWorkflowStatusFilterChange={setTaskWorkflowStatusFilter}
            currentTaskId={taskEditId ?? null}
            taskName={taskName}
            vendorId={vendorId ?? null}
            decorElementId={decorElementId ?? null}
            decorElementCategory={decorElementCategory}
            decorElementName={decorElementName}
            stationeryItemId={stationeryItemId ?? null}
            onAddTask={() => navigate('/task/new')}
            onAddSong={() => navigate('/song/new')}
            musicTab={musicTab}
            onMusicTabChange={setMusicTab}
            menuTab={menuTab}
            onMenuTabChange={setMenuTab}
            menuView={menuView}
            onMenuViewChange={setMenuView}
            logisticsTab={logisticsTab}
            onLogisticsTabChange={handleLogisticsTabChange}
            onVendorRefresh={vendorRefreshFn ?? undefined}
            edTab={edTab}
            onEdTabChange={(tab) => handleEdTabChange(tab as typeof ED_TABS[number])}
            edEventId={edSelectedEventId}
            onEdEventChange={setEdSelectedEventId}
            edEvents={appEvents.map(e => ({ id: e.id, title: e.title, venue: e.venue }))}
            edWaiterMode={edWaiterMode}
            onEdWaiterModeChange={setEdWaiterMode}
            edKbViewMode={edKbViewMode}
            onEdKbViewModeChange={setEdKbViewMode}
            edPhase={edPhase}
            onEdPhaseChange={setEdPhase}
            edActiveTab={edTab}
            onGlobalRefresh={triggerSync}
            isSyncing={syncing}
            calendarViewMode={calendarControls?.viewMode}
            onCalendarViewModeChange={calendarControls?.setViewMode}
            calendarLabel={calendarControls?.label}
            onCalendarPrev={calendarControls?.prev}
            onCalendarNext={calendarControls?.next}
            onCalendarToday={calendarControls?.today}
            onSaveSettings={settingsActions?.save ? async () => { await settingsActions.save!(); showSuccess('Settings saved'); } : undefined}
            onResetSettings={settingsActions?.reset}
            settingsHasChanges={settingsActions?.hasUnsavedChanges}
            settingsSaving={settingsActions?.saving}
          />

          <main className="main-content">
              {activeView === 'dashboard' && <Dashboard />}
              {GUESTS_ENABLED && activeView === 'guests' && <GuestsPage />}

              {/* ── Create form routes ── */}
              {activeView === 'task-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <TaskFormPage
                    onClose={() => navigate('/activities')}
                    // A failure is left to throw: the form shows a 422's field errors
                    // under their fields (or a toast) and stays open.
                    onSave={async (data) => {
                      await createTask(data);
                      showSuccess('Task created');
                      navigate('/activities');
                    }}
                  />
                </div></div>
              )}

              {activeView === 'task-edit' && taskEditId && isLoadingEditTask && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <LoadingSpinner message="Loading task…" fullPage />
                </div></div>
              )}

              {activeView === 'task-edit' && taskEditId && !isLoadingEditTask && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <TaskFormPage
                    task={editingTask}
                    onClose={() => navigate('/activities')}
                    onSave={async (data) => {
                      await updateTask(taskEditId, data);
                      showSuccess('Task updated');
                      navigate('/activities');
                    }}
                    onNameLoad={setTaskName}
                  />
                </div></div>
              )}

              {activeView === 'vehicle-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <VehicleFormPage onSave={() => { showSuccess('Vehicle saved'); navigate('/logistics'); }} onCancel={() => navigate('/logistics')} />
                </div></div>
              )}

              {activeView === 'stationery-item-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <StationeryItemFormPage onSave={() => { showSuccess('Stationery item saved'); navigate('/stationery'); }} onCancel={() => navigate('/stationery')} />
                </div></div>
              )}

              {activeView === 'stationery-item-edit' && stationeryItemId && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <StationeryItemFormPage itemId={stationeryItemId} onSave={() => { showSuccess('Stationery item saved'); navigate('/stationery'); }} onCancel={() => navigate('/stationery')} onNameLoad={setStationeryItemName} onArchivedLoad={setStationeryItemArchived} />
                </div></div>
              )}

              {budgetVisible && activeView === 'budget-category-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <BudgetCategoryFormPage onSave={() => { showSuccess('Budget category saved'); navigate('/budget'); }} onCancel={() => navigate('/budget')} />
                </div></div>
              )}

              {activeView === 'song-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <SongFormPage
                    ref={songFormRef}
                    onSave={() => { showSuccess('Song saved'); navigate('/music'); }}
                    onCancel={() => navigate('/music')}
                    onValidityChange={setSongFormCanSave}
                  />
                </div></div>
              )}

              {activeView === 'driver-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <DriverFormPage onSave={() => { showSuccess('Driver saved'); navigate('/logistics'); }} onCancel={() => navigate('/logistics')} />
                </div></div>
              )}


              {activeView === 'menu-item-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <MenuItemFormPage onSave={() => { showSuccess('Menu item saved'); navigate('/menu'); }} onCancel={() => navigate('/menu')} defaultTab={menuTab} />
                </div></div>
              )}

              {activeView === 'menu-item-edit' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <MenuItemFormPage editMode menuItemId={menuItemId ?? undefined} onSave={() => { showSuccess('Menu item saved'); navigate('/menu'); }} onCancel={() => navigate('/menu')} onNameLoad={setMenuItemName} />
                </div></div>
              )}

              {activeView === 'calendar-event-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <CalendarEventFormPage
                    prefillDate={new URLSearchParams(location.search).get('date') || undefined}
                    prefillStartTime={new URLSearchParams(location.search).get('time') || undefined}
                    onSave={() => { showSuccess('Calendar event saved'); navigate('/calendar'); }}
                    onCancel={() => navigate('/calendar')}
                  />
                </div></div>
              )}

              {activeView === 'calendar-event-edit' && calendarEventId && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <CalendarEventFormPage
                    eventId={calendarEventId}
                    onSave={() => { showSuccess('Calendar event saved'); navigate('/calendar'); }}
                    onCancel={() => navigate('/calendar')}
                  />
                </div></div>
              )}

              {activeView === 'timeline' && (
                <ProjectTimeline
                    view={timelineView}
                    onViewChange={setTimelineView}
                    rosEventFilter={rosEventFilter}
                    onRosEventTitlesChange={setRosEventTitles}
                />
            )}

              {ASOEBI_ENABLED && (activeView in ASOEBI_TAB_BY_VIEW) && (
                <AsoebiAccessGate>
                <AsoebiManager
                  activeTab={ASOEBI_TAB_BY_VIEW[activeView]}
                  onTabChange={(tab: AsoebiTab) => navigate(`/${ASOEBI_VIEW_BY_TAB[tab]}`)}
                  fabricId={fabricDetailId}
                  openBundleId={bundleId}
                  openNewBundleOnMount={bundleIsNewRoute}
                />
                </AsoebiAccessGate>
              )}

              {ASOEBI_ENABLED && activeView === 'asoebi-assignments' && <AsoebiAssignmentsView />}

              {activeView === 'asoebi' && (
                <BundlesView
                  openBundleId={bundleId}
                  openNewBundleOnMount={bundleIsNewRoute}
                />
              )}

              {activeView === 'vehicle-detail' && (
                <LogisticsManager
                  vehicleAction="edit"
                  vehicleId={vehicleId}
                  onVehicleNameChange={setVehicleName}
                  activeTab={logisticsTab}
                  onTabChange={setLogisticsTab}
                />
              )}

              {activeView === 'driver-detail' && (
                <LogisticsManager
                  driverAction="edit"
                  driverId={driverId}
                  onDriverNameChange={setDriverName}
                  activeTab={logisticsTab}
                  onTabChange={setLogisticsTab}
                />
              )}

              {activeView === 'logistics' && (
                <LogisticsManager
                  activeTab={logisticsTab}
                  onTabChange={handleLogisticsTabChange}
                  triggerAddAssignment={triggerAddAssignment}
                  onAddAssignmentHandled={() => setTriggerAddAssignment(false)}
                  onAssignmentFormOpenChange={(open, isEditing) => {
                    setLogisticsAssignmentFormOpen(open);
                    setLogisticsAssignmentIsEditing(!!isEditing);
                    if (!open) setAssignmentFormStatus('pending');
                  }}
                  assignmentStatusOverride={logisticsAssignmentFormOpen ? assignmentFormStatus : undefined}
                  onAssignmentStatusChange={setAssignmentFormStatus}
                />
              )}

              {activeView === 'menu' && <MenuView menuTab={menuTab} onMenuTabChange={setMenuTab} menuView={menuView} onMenuViewChange={setMenuView} />}

              {activeView === 'calendar' && (
                <CalendarView
                  onControlsReady={setCalendarControls}
                  onAddEvent={(prefill) => navigate(`/calendar/event/new?date=${encodeURIComponent(prefill.date)}&time=${encodeURIComponent(prefill.startTime)}`)}
                  onEditEvent={(id) => navigate(`/calendar/event/${fmtId('CE', id)}`)}
                />
              )}

              {activeView === 'communications' && <CommunicationsRoute />}

              {activeView === 'team' && <TeamRoute />}
              {activeView === 'team-member-form' && TEAM_ENABLED && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <TeamMemberFormPage key={location.pathname} onSave={() => navigate('/team')} onCancel={() => navigate('/team')} />
                </div></div>
              )}

              {activeView === 'vendor-compare' && (
                <VendorComparison
                  vendors={vendors}
                  onClose={() => navigate('/vendors')}
                  isPage
                />
              )}

              {activeView === 'vendors' && (
                <>
                  <VendorTable
                    vendors={filteredVendors}
                    onVendorClick={handleVendorClick}
                    onRateVendor={handleRateVendor}
                    viewMode={vendorView}
                    onViewModeChange={setVendorView}
                    showArchived={vendorStatusFilter === 'archived'}
                  />
                </>
              )}

              {activeView === 'vendor-detail' && <VendorTable
                vendors={filteredVendors}
                onVendorClick={handleVendorClick}
                onRateVendor={handleRateVendor}
                vendorId={vendorId}
              />}

              {activeView === 'vendor-profile' && vendorProfileId && (() => {
                const v = vendors.find(vv => vv.id === vendorProfileId);
                return v ? (
                  <div className="wt-container fade-in"><div className="dashboard-main">
                    <VendorFormPage vendor={v} onClose={() => navigate('/vendors')} />
                  </div></div>
                ) : null;
              })()}

              {activeView === 'vendor-rating' && vendorRatingId && (() => {
                const v = vendors.find(vv => vv.id === vendorRatingId);
                return v ? (
                  <div className="wt-container fade-in"><div className="dashboard-main">
                    <VendorFormPage vendor={v} onClose={() => navigate('/vendors')} />
                  </div></div>
                ) : null;
              })()}

              {activeView === 'vendor-suggestion' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <VendorFormPage
                    onClose={() => navigate('/vendors')}
                    onSubmit={handleVendorSuggestionSubmit}
                  />
                </div></div>
              )}

              {budgetVisible && activeView === 'budget-variances' && (
                <BudgetVarianceTracker />
              )}
              {budgetVisible && activeView === 'budget' && (
                <ProtectedRoute>
                  <BudgetTracker
                    vendors={vendors}
                    shippingImportData={shippingExportData}
                    onShippingImported={() => setShippingExportData(null)}
                    eventFilter={parseId('EV', new URLSearchParams(location.search).get('event')) ?? budgetEventFilter}
                    onEventFilterChange={(next) => {
                      setBudgetEventFilter(next);
                      const params = new URLSearchParams(location.search);
                      if (next) params.set('event', fmtId('EV', next)); else params.delete('event');
                      const qs = params.toString();
                      navigate(`/budget${qs ? `?${qs}` : ''}`, { replace: true });
                    }}
                    budgetView={budgetView}
                    onBudgetViewChange={setBudgetView}
                    triggerAddCategory={triggerAddBudgetCategory}
                    onAddCategoryHandled={() => setTriggerAddBudgetCategory(false)}
                  />
                </ProtectedRoute>
              )}

              {budgetVisible && activeView === 'budget-category' && budgetCategoryId && (
                <ProtectedRoute>
                  <BudgetTracker
                    vendors={vendors}
                    shippingImportData={null}
                    onShippingImported={() => {}}
                    eventFilter={budgetEventFilter}
                    onEventFilterChange={setBudgetEventFilter}
                    budgetView={budgetView}
                    onBudgetViewChange={setBudgetView}
                    triggerAddCategory={false}
                    onAddCategoryHandled={() => {}}
                    categoryId={budgetCategoryId}
                    budgetCategoryTab={budgetCategoryTab}
                    onBudgetCategoryTabChange={setBudgetCategoryTab}
                    triggerAddBudgetVendor={triggerAddBudgetVendor}
                    onAddBudgetVendorHandled={() => setTriggerAddBudgetVendor(false)}
                    triggerSaveBudgetCategory={triggerSaveBudgetCategory}
                    onSaveBudgetCategoryHandled={() => setTriggerSaveBudgetCategory(false)}
                    onVendorEditStateChange={setIsEditingBudgetVendor}
                    triggerSaveBudgetVendor={triggerSaveBudgetVendor}
                    onSaveBudgetVendorHandled={() => setTriggerSaveBudgetVendor(false)}
                    triggerCancelBudgetVendor={triggerCancelBudgetVendor}
                    onCancelBudgetVendorHandled={() => setTriggerCancelBudgetVendor(false)}
                  />
                </ProtectedRoute>
              )}

              {activeView === 'calculator' && (
                <ProtectedRoute>
                  <BudgetCalculator />
                </ProtectedRoute>
              )}

              {PROCUREMENT_ENABLED && activeView === 'shipping' && (
                <ProtectedRoute>
                  <ShippingAgentsView />
                </ProtectedRoute>
              )}
              {GIFT_REGISTRY_ENABLED && activeView === 'gift-registry' && <ProtectedRoute><GiftRegistryList /></ProtectedRoute>}
              {GIFT_REGISTRY_ENABLED && activeView === 'gift-registry-new' && <ProtectedRoute><GiftItemPage key="new" /></ProtectedRoute>}
              {GIFT_REGISTRY_ENABLED && activeView === 'gift-registry-item' && (
                <ProtectedRoute>
                  {giftRef !== null
                    ? <GiftItemPage key={giftRef} refNumber={giftRef} />
                    : <EmptyState icon="gift" title="This address is not a gift registry item" description="Items are addressed like /gift-registry/GR-7." />}
                </ProtectedRoute>
              )}
              {VENDOR_BOOKINGS_ENABLED && activeView === 'vendor-bookings' && <ProtectedRoute><VendorBookingsList /></ProtectedRoute>}
              {VENDOR_BOOKINGS_ENABLED && activeView === 'vendor-booking-edit' && vendorBookingId && <ProtectedRoute><VendorBookingEdit key={vendorBookingId} bookingId={vendorBookingId} /></ProtectedRoute>}
              {PROCUREMENT_ENABLED && activeView === 'procurement-orders' && <ProtectedRoute><ProcurementTracker /></ProtectedRoute>}
              {PROCUREMENT_ENABLED && activeView === 'procurement-order-new' && <ProtectedRoute><ProcurementOrderEdit /></ProtectedRoute>}
              {PROCUREMENT_ENABLED && activeView === 'procurement-order-edit' && procurementOrderId && <ProtectedRoute><ProcurementOrderEdit key={procurementOrderId} orderId={procurementOrderId} /></ProtectedRoute>}
              {PROCUREMENT_ENABLED && activeView === 'procurement-shipments' && <ProtectedRoute><ShipmentsView /></ProtectedRoute>}
              {PROCUREMENT_ENABLED && activeView === 'procurement-shipment-new' && <ProtectedRoute><ShipmentEdit /></ProtectedRoute>}
              {PROCUREMENT_ENABLED && activeView === 'procurement-shipment-edit' && procurementShipmentId && <ProtectedRoute><ShipmentEdit key={procurementShipmentId} shipmentId={procurementShipmentId} /></ProtectedRoute>}

              {activeView === 'settings' && (
                <ProtectedRoute>
                  <Settings onActionsReady={setSettingsActions} />
                </ProtectedRoute>
              )}

              {activeView === 'music' && <MusicView musicTab={musicTab} onMusicTabChange={setMusicTab} />}
              {activeView === 'events' && <EventsList />}
              {activeView === 'event-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <EventFormPage
                    onSave={saved => { showSuccess('Event saved'); navigate(entityPath('event', saved.id) ?? '/events'); }}
                    onCancel={() => navigate('/events')}
                  />
                </div></div>
              )}
              {activeView === 'event-detail' && eventId && (
                <EventDetail eventId={eventId} onBack={() => navigate('/events')} />
              )}
              {activeView === 'guidelines' && <GuidelinesManager />}
              {activeView === 'guideline-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <GuidelineFormPage onSave={() => { showSuccess('Guideline saved'); navigate('/guidelines'); }} onCancel={() => navigate('/guidelines')} />
                </div></div>
              )}
              {activeView === 'guideline-edit' && guidelineId && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <GuidelineFormPage guidelineId={guidelineId} onSave={() => { showSuccess('Guideline saved'); navigate('/guidelines'); }} onCancel={() => navigate('/guidelines')} />
                </div></div>
              )}
              {activeView === 'activities' && (
                <ActivityTracker
                  filterTab={activityFilter}
                  onFilterTabChange={setActivityFilter}
                  viewMode={activityView}
                  onViewModeChange={handleActivityViewChange}
                  triggerAddTask={triggerAddTask}
                  onAddTaskHandled={() => setTriggerAddTask(false)}
                  myTasksOnly={myTasksOnly}
                  priorityFilters={priorityFilters}
                  assigneeFilters={assigneeFilters}
                  statusFilter={taskStatusFilter}
                  workflowStatusFilter={taskWorkflowStatusFilter}
                />
              )}
              {activeView === 'stationery' && (
                <StationeryTracker
                  activeCategory={stationeryCategory}
                  onCategoryChange={setStationeryCategory}
                  filterStatus={stationeryStatus}
                  onFilterStatusChange={setStationeryStatus}
                  showArchived={stationeryArchived}
                  viewMode={stationeryView}
                  onViewModeChange={setStationeryView}
                  triggerAddItem={triggerAddItem}
                  onAddItemHandled={() => setTriggerAddItem(false)}
                  onItemClick={(item) => navigate(`/stationery-item/${fmtId('ST', item.id)}`)}
                />
              )}
              {activeView === 'decor' && (
                <DecorPlanner
                  categoryFilter={decorCategoryFilter}
                  statusFilter={decorStatusFilter}
                  eventFilter={decorEventFilter || undefined}
                />
              )}
              {activeView === 'decor-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <DecorElementFormPage
                    defaultCategory={new URLSearchParams(location.search).get('category') ?? undefined}
                    onSave={() => { showSuccess('Décor element saved'); navigate('/decor'); }}
                    onCancel={() => navigate('/decor')}
                  />
                </div></div>
              )}
              {activeView === 'decor-edit' && decorElementId && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <DecorElementFormPage
                    decorId={decorElementId}
                    onSave={() => { showSuccess('Décor element saved'); navigate('/decor'); }}
                    onCancel={() => navigate('/decor')}
                  />
                </div></div>
              )}
              {PROCUREMENT_ENABLED && activeView === 'procurement' && <ProtectedRoute><InventoryTracker /></ProtectedRoute>}
              {activeView === 'payment-record' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <RecordPaymentFormPage
                    preselectedId={new URLSearchParams(location.search).get('vendor') || undefined}
                    onSave={() => navigate('/payments')}
                    onCancel={() => navigate('/payments')}
                  />
                </div></div>
              )}
              {activeView === 'contributor-form' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <ContributorFormPage
                    onSave={() => navigate('/payments/contributors')}
                    onCancel={() => navigate('/payments/contributors')}
                  />
                </div></div>
              )}
              {activeView === 'payments' && (
                <PaymentsView
                  filterStatus={paymentFilterStatus}
                  onFilterStatusChange={setPaymentFilterStatus}
                  sortField={paymentSortField}
                  onSortFieldChange={setPaymentSortField}
                  paymentsTab={paymentsTab}
                  onPaymentsTabChange={handlePaymentsTabChange}
                  eventFilter={paymentsEventFilter}
                />
              )}
              {activeView === 'event-day' && (
                <EventDayDashboard
                  activeTab={edTab}
                  onVendorOrderCount={setVendorOrderCount}
                  onVendorRefreshRef={fn => setVendorRefreshFn(() => fn)}
                  selectedEventId={edSelectedEventId}
                  onSelectedEventChange={setEdSelectedEventId}
                  waiterMode={edWaiterMode}
                  onWaiterModeChange={setEdWaiterMode}
                  heatmap={edHeatmap}
                  onHeatmapChange={setEdHeatmap}
                  kbViewMode={edKbViewMode}
                  onKbViewModeChange={setEdKbViewMode}
                  phase={edPhase}
                  onPhaseChange={setEdPhase}
                />
              )}
              {activeView === 'venue' && <VenueManager statusFilter={venueStatusFilter} />}
              {activeView === 'venue-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <VenueFormPage
                    onCancel={() => navigate('/venue')}
                    onSave={(id) => navigate(entityPath('venue', id) ?? '/venues')}
                  />
                </div></div>
              )}
              {activeView === 'venue-detail' && <VenueManager venueId={venueId} />}

              {activeView === 'history' && <HistoryPage />}

              {(activeView === 'quotes' || activeView === 'quote-detail' || activeView === 'quote-new') && (
                <QuotesTracker vendors={vendors} quoteId={quoteDetailId} newQuote={activeView === 'quote-new'} />
              )}

              {PROCUREMENT_ENABLED && activeView === 'shipping-agent-edit' && shippingAgentId && <ShippingAgentEdit key={shippingAgentId} agentId={shippingAgentId} />}

              {activeView === 'catalogue' && <ProductCatalog />}

              {activeView === 'catalogue-new' && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <CatalogItemFormPage onSave={() => { showSuccess('Item saved'); navigate('/catalogue'); }} onCancel={() => navigate('/catalogue')} />
                </div></div>
              )}

              {activeView === 'catalogue-edit' && catalogueItemId && (
                <div className="wt-container fade-in"><div className="dashboard-main">
                  <CatalogItemFormPage productId={catalogueItemId} onSave={() => { showSuccess('Item saved'); navigate('/catalogue'); }} onCancel={() => navigate('/catalogue')} onNameLoad={setCatalogueItemName} />
                </div></div>
              )}

          </main>

          {/* Vendor Suggestion, Rating, and Profile are now full-page views via routing */}

          {/* Vendor Comparison is now a full page at /vendors/compare */}

        </div>
      </div>
      {mobileSidebarOpen && (
        <div
          className="mobile-sidebar-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <ConfirmDialog
        open={deleteBudgetCategoryConfirm}
        onClose={() => setDeleteBudgetCategoryConfirm(false)}
        onConfirm={() => {
          setBudgetCategories(prev => prev.filter(c => c.id !== budgetCategoryId));
          if (budgetCategoryId) void apiDeleteBudgetCategory(budgetCategoryId);
          navigate('/budget');
          setDeleteBudgetCategoryConfirm(false);
        }}
        title="Delete budget category?"
        description="This cannot be undone."
      />
    </SearchProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <RecordRefsLoader />
        <SettingsProvider>
          <NotificationProvider>
            <ConfirmProvider>
            <AuditLogProvider>
              <SubscriptionGate>
                <AppContent />
              </SubscriptionGate>
            </AuditLogProvider>
            </ConfirmProvider>
          </NotificationProvider>
        </SettingsProvider>
      </ProjectProvider>
    </AuthProvider>
  );
}

export default App;
